import React, {useMemo} from "react";
import {Button, Typography} from "@mui/material";
import CustomText from "../../../widgets/inputs/CustomText";
import AutocompleteSelect from "../../../widgets/inputs/AutocompleteSelect";
import {getCategoryByLabel, getCompanyInfo} from "../../../../utils/GenericUtils";
import {valueRegisterNone} from "../../../../utils/RegisterUtils";

const levelOptions = [
	{value: 0, label: "Base", role: "ROLE_0"},
	{value: 1, label: "Intermedio", role: "ROLE_1"},
	{value: 2, label: "Avanzato", role: "ROLE_2"},
	{value: 3, label: "Medico", role: "ROLE_3"},
	{value: 4, label: "Amministratore", role: "ROLE_4"},
];

const ticketTypeOptions = [
	{value: "INT", label: "Interni"},
	{value: "EXT", label: "Form online"},
	{value: "CCM", label: "Callcenter"},
];

const filterFieldSize = "small";

const hasLevelAccess = (roles, role) => (
	roles?.includes(role) ||
	roles?.includes("ROLE_SUPERADMIN") ||
	roles?.includes("ROLE_MASTER_LEVEL")
);

const FilterGroup = ({classes, title, children, fieldsClassName}) => (
	<div className={classes.filterGroup}>
		<Typography className={classes.filterGroupTitle}>{title}</Typography>
		<div className={[classes.filterGroupFields, fieldsClassName].filter(Boolean).join(" ")}>
			{children}
		</div>
	</div>
);

const TicketFiltersPanel = ({
	classes,
	control,
	errors,
	level,
	category,
	subCategory,
	ticketFrom,
	categoriesV3,
	users,
	loadingUsers,
	roles,
	onLevelChange,
	onCategoryChange,
	onSubCategoryChange,
	onTicketFromChange,
	onApply,
	onClear,
}) => {
	const categoryOptions = useMemo(
		() => Object.entries(categoriesV3 || {}).map(([, value]) => ({value: value.label, label: value.label})),
		[categoriesV3]
	);
	const subCategoryOptions = useMemo(
		() => (getCategoryByLabel(category ?? "", categoriesV3 || {})?.subCategories || [])
			.map((value) => ({value: value.label, label: value.label})),
		[category, categoriesV3]
	);
	const operatorOptions = useMemo(
		() => (users || [])
			.filter((user) => user.trusted)
			.sort((a, b) => a.alias.localeCompare(b.alias))
			.map((user) => ({
				value: `${user.alias} (${getCompanyInfo()?.nome})`,
				label: `${user.alias} (${getCompanyInfo()?.nome})`,
			})),
		[users]
	);

	return (
		<div className={classes.filterPanel}>
			<div className={classes.filterGroupWide}>
				<FilterGroup classes={classes} title="Ticket">
					<div className={classes.filterField}>
						<CustomText
							control={control}
							rules={{required: false}}
							label="Id ticket"
							autoFocus={false}
							name="search.id"
							size={filterFieldSize}
							error={errors["search"]?.["id"]}
						/>
					</div>
					<div className={classes.filterField}>
						<CustomText
							control={control}
							rules={{required: false}}
							label="Hashtag"
							autoFocus={false}
							name="search.hashtag"
							size={filterFieldSize}
							error={errors["search"]?.["hashtag"]}
						/>
					</div>
					<div className={classes.filterField}>
						<AutocompleteSelect
							control={control}
							name="search.lastModify"
							defaultValue={null}
							disabled={loadingUsers}
							label="Operatore ultima modifica"
							options={operatorOptions}
							rules={{required: false}}
							size={filterFieldSize}
							error={errors["search"]?.["lastModify"]}
							helperText={errors["search"]?.["lastModify"] ? "Campo non valido" : ""}
						/>
					</div>
				</FilterGroup>
			</div>

			<div className={classes.filterGroupWide}>
				<FilterGroup classes={classes} title="Soggetto" fieldsClassName={classes.filterGroupFieldsFour}>
					<div className={classes.filterField}>
						<CustomText
							control={control}
							rules={{required: false}}
							label="Codice fiscale"
							autoFocus={false}
							name="search.anagraficaToSearch.codF"
							size={filterFieldSize}
							error={errors["search"]?.["anagraficaToSearch"]?.["codF"]}
						/>
					</div>
					<div className={classes.filterField}>
						<CustomText
							control={control}
							rules={{required: false}}
							label="Cellulare"
							autoFocus={false}
							name="search.anagraficaToSearch.cell"
							size={filterFieldSize}
							error={errors["search"]?.["anagraficaToSearch"]?.["cell"]}
						/>
					</div>
					<div className={classes.filterField}>
						<CustomText
							control={control}
							rules={valueRegisterNone}
							label="Nome"
							autoFocus={false}
							name="search.anagraficaToSearch.nome"
							size={filterFieldSize}
							error={errors["search"]?.["anagraficaToSearch"]?.["nome"]}
						/>
					</div>
					<div className={classes.filterField}>
						<CustomText
							control={control}
							rules={valueRegisterNone}
							label="Cognome"
							autoFocus={false}
							name="search.anagraficaToSearch.cognome"
							size={filterFieldSize}
							error={errors["search"]?.["anagraficaToSearch"]?.["cognome"]}
						/>
					</div>
				</FilterGroup>
			</div>

			<div className={classes.filterGroupWide}>
				<FilterGroup classes={classes} title="Classificazione" fieldsClassName={classes.filterGroupFieldsFour}>
					<div className={classes.filterField}>
						<AutocompleteSelect
							label="Livello"
							options={levelOptions}
							size={filterFieldSize}
							value={level}
							onChange={onLevelChange}
							getOptionDisabled={(option) => !hasLevelAccess(roles, option.role)}
						/>
					</div>
					<div className={classes.filterField}>
						<AutocompleteSelect
							label="Categoria"
							options={categoryOptions}
							size={filterFieldSize}
							value={category}
							onChange={onCategoryChange}
						/>
					</div>
					<div className={classes.filterField}>
						<AutocompleteSelect
							disabled={!category}
							label="Sottocategoria"
							options={subCategoryOptions}
							size={filterFieldSize}
							value={subCategory}
							onChange={onSubCategoryChange}
						/>
					</div>
					<div className={classes.filterField}>
						<AutocompleteSelect
							label="Tipo ticket"
							options={ticketTypeOptions}
							size={filterFieldSize}
							value={ticketFrom}
							onChange={onTicketFromChange}
						/>
					</div>
				</FilterGroup>
			</div>

			<div className={classes.filterActions}>
				<Button variant="text" disableElevation onClick={onClear}>
					Rimuovi filtri
				</Button>
				<Button variant="contained" disableElevation color="primary" onClick={onApply}>
					Cerca
				</Button>
			</div>
		</div>
	);
};

export default React.memo(TicketFiltersPanel);
